import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import { DrawerV2, ButtonV2, ChipV2, dsV2 } from '@bloomays-lib/ui.shared';
import type { IContact, ContactSide } from '@bloomays-lib/types.shared';

import { SectionHeading } from '../formFields';
import { BloomerRow } from '../components';
import { resolveContactGroup, type ContactSlot } from '../../../../helpers/missionDashboardV2';
import { useContactMutations } from '../../../../hooks/useContactMutations';
import type { ContactSource } from './ContactEditDrawer';

/** Données d'un emplacement de contact pour les vues groupées (cartes + manage-all). */
export type ContactGroupData = {
  slot: ContactSlot;
  label: string;
  emptyHint: string;
  missionContacts?: (IContact | null | undefined)[];
  societyContacts?: (IContact | null | undefined)[];
  societyId?: string;
  societyName?: string;
};

export type ManageAllContactsDrawerProps = {
  open: boolean;
  onClose: () => void;
  missionId: string;
  groups: ContactGroupData[];
  onDone?: () => void | Promise<unknown>;
  onEditContact?: (
    contact: IContact,
    slot: ContactSlot,
    source: ContactSource,
    societyId?: string,
    societyName?: string,
  ) => void;
  onOpenSociety?: (side: ContactSide) => void;
  /** Restreint l'affichage à un seul côté (ouverture « Gérer tout » d'une carte). */
  side?: ContactSide;
  /** Bloomer (personne) affiché en tête du côté fournisseur. */
  bloomer?: { fullname?: string; firstname?: string; lastname?: string; email?: string; phoneNumber?: string } | null;
  bloomerSubtitle?: string;
  onEditBloomer?: () => void;
};

const contactName = (c: IContact) =>
  c.fullname || `${c.firstname ?? ''} ${c.lastname ?? ''}`.trim() || c.email || 'Contact';

const clean = (list?: (IContact | null | undefined)[]): IContact[] =>
  (list ?? []).filter((c): c is IContact => Boolean(c));

/**
 * Gestion globale des contacts de la mission (§8) : récapitulatif par côté (fournisseur / client),
 * chaque emplacement listant ses contacts niveau mission (prioritaire) et niveau société (repli).
 * Promotion = liaison mission additive (décision spec #1, le lien société est conservé) ; rétrograder
 * / retirer = déliaison mission. La déliaison société (impact global) passe par la fiche contact.
 */
export const ManageAllContactsDrawer = ({
  open,
  onClose,
  missionId,
  groups,
  onDone,
  onEditContact,
  onOpenSociety,
  side,
  bloomer,
  bloomerSubtitle,
  onEditBloomer,
}: ManageAllContactsDrawerProps) => {
  const { t } = useTranslation(['missionDashboard', 'notify']);

  const mutations = useContactMutations({ missionId, onDone });

  const renderRow = (contact: IContact, group: ContactGroupData, level: ContactSource) => {
    const contactId = contact.recordId ?? '';
    return (
      <Box
        key={`${group.slot.key}-${level}-${contactId || contactName(contact)}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 0',
          borderTop: `1px solid ${dsV2.color.borderLight}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: dsV2.color.noir }}>{contactName(contact)}</Box>
            <ChipV2
              label={
                level === 'society'
                  ? t('drawers.manageContacts.badgeSociety', { ns: 'missionDashboard' })
                  : t('drawers.manageContacts.badgeMission', { ns: 'missionDashboard' })
              }
              tone={level === 'society' ? 'warning' : 'info'}
              size="small"
            />
          </Box>
          {contact.email ? (
            <Box sx={{ fontSize: 11, color: dsV2.color.t4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {contact.email}
            </Box>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
          {level === 'society' && group.slot.allowMission ? (
            <ButtonV2
              variant="secondary"
              size="small"
              onClick={() => contactId && mutations.linkToMission(contactId, group.slot.missionRole, group.slot.side)}
              disabled={mutations.busy || !contactId}
              startIcon={<ArrowUpwardRoundedIcon sx={{ fontSize: 14 }} />}
            >
              {t('drawers.manageContacts.promote', { ns: 'missionDashboard' })}
            </ButtonV2>
          ) : null}
          {level === 'mission' ? (
            <ButtonV2
              variant="secondary"
              size="small"
              onClick={() =>
                contactId && mutations.unlinkFromMission(contactId, group.slot.missionRole, group.slot.side)
              }
              disabled={mutations.busy || !contactId}
              startIcon={<LinkOffRoundedIcon sx={{ fontSize: 14 }} />}
            >
              {t('drawers.manageContacts.remove', { ns: 'missionDashboard' })}
            </ButtonV2>
          ) : null}
          {onEditContact ? (
            <ButtonV2
              variant="secondary"
              size="small"
              onClick={() => onEditContact(contact, group.slot, level, group.societyId, group.societyName)}
              startIcon={<EditRoundedIcon sx={{ fontSize: 14 }} />}
            >
              {t('drawers.manageContacts.edit', { ns: 'missionDashboard' })}
            </ButtonV2>
          ) : null}
        </Box>
      </Box>
    );
  };

  const renderGroup = (group: ContactGroupData) => {
    const mission = clean(group.missionContacts);
    const society = clean(group.societyContacts);
    const resolved = resolveContactGroup<IContact>({
      missionContacts: group.missionContacts,
      societyContacts: group.societyContacts,
    });
    return (
      <Box key={group.slot.key} sx={{ marginBottom: '14px' }}>
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: dsV2.color.t3,
            marginBottom: '2px',
          }}
        >
          {group.label}
        </Box>
        {resolved.contacts.length === 0 ? (
          <Box sx={{ fontSize: '11.5px', color: dsV2.color.t4, fontStyle: 'italic', padding: '6px 0' }}>
            {group.emptyHint}
          </Box>
        ) : (
          <>
            {mission.map((contact) => renderRow(contact, group, 'mission'))}
            {society.map((contact) => renderRow(contact, group, 'society'))}
          </>
        )}
      </Box>
    );
  };

  const sections: { side: ContactSide; label: string }[] = side
    ? [
        {
          side,
          label:
            side === 'supplier'
              ? t('drawers.manageContacts.supplier', { ns: 'missionDashboard' })
              : t('drawers.manageContacts.client', { ns: 'missionDashboard' }),
        },
      ]
    : [
        { side: 'supplier', label: t('drawers.manageContacts.supplier', { ns: 'missionDashboard' }) },
        { side: 'client', label: t('drawers.manageContacts.client', { ns: 'missionDashboard' }) },
      ];
  const bloomerName = bloomer
    ? bloomer.fullname || `${bloomer.firstname ?? ''} ${bloomer.lastname ?? ''}`.trim() || 'Bloomer'
    : '';

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title={t('drawers.manageContacts.title', { ns: 'missionDashboard' })}
      subtitle={t('drawers.manageContacts.subtitle', { ns: 'missionDashboard' })}
      width={560}
      footer={
        <ButtonV2 variant="secondary" onClick={onClose}>
          {t('drawers.common.close', { ns: 'missionDashboard' })}
        </ButtonV2>
      }
    >
      {sections.map((section, index) => (
        <Box key={section.side} sx={{ marginTop: index > 0 ? '8px' : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <SectionHeading>{section.label}</SectionHeading>
            {onOpenSociety ? (
              <Box sx={{ marginLeft: 'auto' }}>
                <ButtonV2
                  variant="secondary"
                  size="small"
                  onClick={() => onOpenSociety(section.side)}
                  startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 14 }} />}
                >
                  {t('drawers.manageContacts.society', { ns: 'missionDashboard' })}
                </ButtonV2>
              </Box>
            ) : null}
          </Box>
          {section.side === 'supplier' && bloomer ? (
            <Box
              sx={{
                marginBottom: '12px',
                border: `1px solid ${dsV2.color.borderLight}`,
                borderRadius: dsV2.radius.sm,
                overflow: 'hidden',
              }}
            >
              <BloomerRow
                name={bloomerName}
                subtitle={bloomerSubtitle}
                email={bloomer.email}
                phoneNumber={bloomer.phoneNumber}
                onEdit={onEditBloomer}
              />
            </Box>
          ) : null}
          {groups.filter((group) => group.slot.side === section.side).map(renderGroup)}
        </Box>
      ))}
    </DrawerV2>
  );
};

export default ManageAllContactsDrawer;
